/** Permissions for YouTube/Vimeo iframe embeds (includes Compute Pressure API). */
export const VIDEO_EMBED_IFRAME_ALLOW =
	"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure *";

/**
 * Parse a YouTube URL (watch, youtu.be, or already-embed form) into an ID.
 * Returns `null` for non-YouTube URLs.
 */
export function getYouTubeEmbedUrl(url: string): string | null {
	try {
		const parsed = new URL(url);
		const host = parsed.hostname.replace(/^www\./, "");

		if (host === "youtu.be") {
			const id = parsed.pathname.slice(1);
			return id ? `https://www.youtube.com/embed/${id}` : null;
		}

		if (host === "youtube.com" || host === "m.youtube.com") {
			if (parsed.pathname === "/watch") {
				const id = parsed.searchParams.get("v");
				return id ? `https://www.youtube.com/embed/${id}` : null;
			}
			if (parsed.pathname.startsWith("/embed/")) {
				return url;
			}
			if (parsed.pathname.startsWith("/shorts/")) {
				const id = parsed.pathname.split("/")[2];
				return id ? `https://www.youtube.com/embed/${id}` : null;
			}
		}
	} catch {
		return null;
	}
	return null;
}

/** Path looks like a direct progressive video file (CDN / static host), not a player page. */
const DIRECT_VIDEO_FILE_PATH = /\.(mp4|webm|ogg|m4v|mov)$/i;

/**
 * True when the URL points at a typical direct file (e.g. `.mp4` on a CDN).
 * Those should use `<video src={url}>` — not an iframe embed.
 */
export function isDirectVideoFileUrl(url: string): boolean {
	try {
		return DIRECT_VIDEO_FILE_PATH.test(new URL(url).pathname);
	} catch {
		return false;
	}
}

/** Return a Vimeo embed URL for `https://vimeo.com/<id>`, or null. */
export function getVimeoEmbedUrl(url: string): string | null {
	try {
		const parsed = new URL(url);
		const host = parsed.hostname.replace(/^www\./, "");
		if (host === "vimeo.com") {
			const id = parsed.pathname.split("/").filter(Boolean)[0];
			return id && /^\d+$/.test(id)
				? `https://player.vimeo.com/video/${id}`
				: null;
		}
		if (host === "player.vimeo.com") {
			return url;
		}
	} catch {
		return null;
	}
	return null;
}

/**
 * YouTube or Vimeo iframe `src`, or `null` to use a native `<video>` element.
 *
 * Returns `null` for direct file URLs (e.g. `https://cdn.example.com/clip.mp4`) and
 * any URL that is not a recognized YouTube/Vimeo embed.
 */
export function getVideoEmbedSrc(url: string): string | null {
	if (isDirectVideoFileUrl(url)) {
		return null;
	}
	return getYouTubeEmbedUrl(url) ?? getVimeoEmbedUrl(url);
}
